import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './MyConsumers.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyConsumers = () => {
  const navigate = useNavigate();
  const { sessionToken, user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [consumers, setConsumers] = useState([]);
  const [selectedConsumers, setSelectedConsumers] = useState([]);
  const [campaignStep, setCampaignStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!user?.is_merchant) {
      navigate('/');
      return;
    }
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API}/merchant/brands`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      if (response.data.success) {
        setBrands(response.data.brands);
        if (response.data.brands.length > 0) {
          setSelectedBrand(response.data.brands[0].id);
          loadConsumers(response.data.brands[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading brands:', error);
      setError('Failed to load brands');
    }
  };

  const loadConsumers = async (brandId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/brands/${brandId}/consumers`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      if (response.data.success) {
        setConsumers(response.data.consumers);
      }
    } catch (error) {
      console.error('Error loading consumers:', error);
      setError('Failed to load consumers');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = (brandId) => {
    setSelectedBrand(brandId);
    setSelectedConsumers([]);
    loadConsumers(brandId);
  };

  const toggleConsumerSelection = (consumer) => {
    const exists = selectedConsumers.find(c => c.userId === consumer.id);
    if (exists) {
      setSelectedConsumers(selectedConsumers.filter(c => c.userId !== consumer.id));
    } else {
      setSelectedConsumers([...selectedConsumers, {
        userId: consumer.id,
        name: consumer.name,
        email: consumer.email,
        albums: consumer.albums
      }]);
    }
  };

  const sendCampaign = async () => {
    // This will be implemented in the next step
    setSuccess('Campaign feature coming soon!');
  };

  const brandName = brands.find(b => b.id === selectedBrand)?.name || '';

  return (
    <Layout>
      <div className="my-consumers-page">
        <div className="consumers-header">
          <button className="back-btn" onClick={() => navigate('/merchant')}>← Dashboard</button>
          <div>
            <h1>👥 My Consumers</h1>
            <p>Consumers who favorited your brand</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Brand Selection */}
        <div className="brand-selector">
          <label>Select Brand:</label>
          <select value={selectedBrand || ''} onChange={(e) => handleBrandChange(e.target.value)}>
            {brands.map(brand => (
              <option key={brand.id} value={brand.id}>{brand.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="loading-state">Loading consumers...</div>
        ) : consumers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h2>No Consumers Yet</h2>
            <p>When consumers favorite your brand, they'll appear here</p>
          </div>
        ) : (
          <>
            <div className="consumers-stats">
              <div className="stat-card">
                <span className="stat-number">{consumers.length}</span>
                <span className="stat-label">Total Consumers</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">{selectedConsumers.length}</span>
                <span className="stat-label">Selected for Campaign</span>
              </div>
            </div>

            <div className="consumers-grid">
              {consumers.map(consumer => (
                <div 
                  key={consumer.id} 
                  className={`consumer-card ${selectedConsumers.find(c => c.userId === consumer.id) ? 'selected' : ''}`}
                  onClick={() => toggleConsumerSelection(consumer)}
                >
                  <div className="consumer-header">
                    <div className="consumer-info">
                      <h3>{consumer.name}</h3>
                      <p className="consumer-email">{consumer.email}</p>
                    </div>
                    <div className="selection-indicator">
                      {selectedConsumers.find(c => c.userId === consumer.id) ? '✓' : ''}
                    </div>
                  </div>
                  <div className="consumer-albums">
                    <p className="albums-label">📁 {consumer.albums?.length || 0} Albums</p>
                    <div className="album-previews">
                      {consumer.albums?.slice(0, 3).map((album, idx) => (
                        <div key={idx} className="album-preview">
                          {album.photos?.length > 0 && (
                            <img src={album.photos[0].image_base64} alt="Preview" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedConsumers.length > 0 && (
              <div className="campaign-actions">
                <button className="btn-primary btn-large" onClick={sendCampaign}>
                  📧 Send Campaign to {selectedConsumers.length} Consumer{selectedConsumers.length > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default MyConsumers;